import React, { useEffect, useState, useRef } from 'react';
import { RouteComponentProps } from '@reach/router';
import Input from '../../components/Input';
import Button from '../../components/Button';
import {
    BusinessUnit,
    useCreateBusinessUnit,
    useGetBusinessUnits,
} from '../../queries/BusinessUnits';
import { TrashCan } from '../../components/Icons';
import Table from '../../components/Table';
import { domain } from '../../d-man';
import { Company, useGetCompanies } from '../../queries/Companies';
import { useCurrentProjectId } from '../../queries/Accounts';
import { useGetPermissions } from '../../queries/Permissions';
import Select from '../../components/Select';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export default function BusinessUnits(props: RouteComponentProps) {
    const businessUnits = useGetBusinessUnits();
    const companies = useGetCompanies();
    const businessUnitsRef = useRef(businessUnits);

    const newBusinessUnit = useCreateBusinessUnit();

    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [companyId, setCompanyId] = useState('');
    const [name, setName] = useState('');
    const [prefix, setPrefix] = useState('');
    const [defaultVat, setDefaultVat] = useState('');
    const [defaultCurrency, setDefaultCurrency] = useState('');
    const [prefixRequisition, setPrefixRequisition] = useState('');

    const projectId = useCurrentProjectId();
    const getPermissions = useGetPermissions('PROJECT', projectId.data);
    const { navigate } = props;
    // REDIRECT IF NOT CORRECT ROLE
    if (
        getPermissions.data &&
        !getPermissions.data?.items.find((p) => p.code === 'Project_Allow_Admin')?.hasAccess &&
        navigate
    ) {
        navigate(`/`);
    }

    useEffect(() => {
        businessUnitsRef.current.reset();
    }, []);

    useEffect(() => {
        if (newBusinessUnit.error) {
            setErrorMessage(newBusinessUnit.error || 'Failed to create new Business Unit');
        }
    }, [newBusinessUnit.error]);

    const post = async () => {
        setIsLoading(true);
        setErrorMessage('');

        if (
            !companyId ||
            !name ||
            !prefix ||
            !defaultVat ||
            !defaultCurrency ||
            !prefixRequisition
        ) {
            setErrorMessage('Please capture all the fields above');
        } else {
            const data = {
                name,
                prefix,
                defaultVat: parseFloat(defaultVat),
                defaultCurrency,
                companyId,
                logo: undefined,
                prefixRequisition,
            };
            await newBusinessUnit.execute(data);
            businessUnits.reset();
        }
        setIsLoading(false);
    };

    const toDetail = (id?: string) => {
        if (props.navigate && id) {
            props.navigate(`/business-units/${id}`);
        }
    };

    const clearFields = () => {
        setErrorMessage('');
        setCompanyId('');
        setName('');
        setPrefix('');
        setDefaultVat('');
        setDefaultCurrency('');
        setPrefixRequisition('');
    };

    /**
     * Does an HTTP Delete to the server for the specified id
     * @param id
     */
    const deleteBusinessUnit = async (id?: string) => {
        const remove = domain.delete(`/businessunits/${id}`);
        await remove.execute();
        businessUnits.reset();
    };

    /**
     * Formats the companies returned from the server for the
     * Typeahead component to render
     */
    const formattedCustomers = () => {
        if (companies && companies.data && companies.data.items.length > 0) {
            return companies.data.items.map((company: Company) => ({
                value: company.id as string,
                label: company.name as string,
            }));
        }
        return [];
    };

    /**
     * Triggers when the user click a search option
     * @param event
     */
    const handleSelectedItem = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const { value } = (event.target as unknown) as { value: string };
        setCompanyId(value);
    };

    return (
        <div className="flex flex-col w-full">
            <div className="flex flex-wrap items-end justify-between gap-4 bg-surface border border-border rounded-xl m-4 p-4">
                <div className="flex flex-wrap items-end gap-4">
                    <div className="text-sm font-semibold text-foreground pb-2 pr-4 border-r border-border">
                        New Business Unit
                    </div>
                    <div className="w-48">
                        <Select
                            label="Company"
                            placeholder="Select Company"
                            options={formattedCustomers()}
                            selected={companyId}
                            onChange={(event) => handleSelectedItem(event)}
                        />
                    </div>
                    <div className="w-40">
                        <Input
                            label="Name"
                            value={name}
                            type="text"
                            onChange={(event) => setName(event.target.value)}
                        />
                    </div>
                    <div className="w-40">
                        <Input
                            label="Order Prefix"
                            value={prefix}
                            type="text"
                            onChange={(event) => setPrefix(event.target.value)}
                        />
                    </div>
                    <div className="w-40">
                        <Input
                            label="Requisition Prefix"
                            value={prefixRequisition}
                            type="text"
                            onChange={(event) => setPrefixRequisition(event.target.value)}
                        />
                    </div>
                    <div className="w-40">
                        <Input
                            label="Default VAT"
                            value={defaultVat}
                            type="text"
                            onChange={(event) => setDefaultVat(event.target.value)}
                        />
                    </div>
                    <div className="w-40">
                        <Input
                            label="Default Currency"
                            value={defaultCurrency}
                            type="text"
                            onChange={(event) => setDefaultCurrency(event.target.value)}
                        />
                    </div>
                </div>

                <div className="flex items-center gap-3">
                    {errorMessage && <div className="text-sm text-danger">{errorMessage}</div>}
                    <Button secondary onClick={clearFields}>
                        Clear
                    </Button>
                    <Button disabled={isLoading} onClick={post}>
                        {!isLoading ? 'Submit' : <div>Loading</div>}
                    </Button>
                </div>
            </div>
            <div className="m-4">
                <Table
                    loading={businessUnits.loading && 'Fetching Business Units from server'}
                    error={
                        businessUnits.error &&
                        `Failed to fetch Business Units list from the server (${businessUnits.error})`
                    }
                    data={businessUnits.data?.items}
                    columns={[
                        {
                            key: 'company.name',
                            title: 'Company',
                            onClick: (businessUnit: BusinessUnit) => toDetail(businessUnit.id),
                        },
                        {
                            key: 'name',
                            title: 'Name',
                            onClick: (businessUnit: BusinessUnit) => toDetail(businessUnit.id),
                        },
                        {
                            key: 'prefix',
                            title: 'Prefix Order',
                            onClick: (businessUnit: BusinessUnit) => toDetail(businessUnit.id),
                        },
                        {
                            key: 'prefixRequisition',
                            title: 'Prefix Requisition',
                            onClick: (businessUnit: BusinessUnit) => toDetail(businessUnit.id),
                        },
                        {
                            key: 'defaultVat.amount',
                            title: 'Default Vat',
                            onClick: (businessUnit: BusinessUnit) => toDetail(businessUnit.id),
                        },
                        {
                            key: 'defaultCurrency.symbol',
                            title: 'Default Currency',
                            onClick: (businessUnit: BusinessUnit) => toDetail(businessUnit.id),
                        },
                        {
                            key: 'delete',
                            title: '',
                            onClick: (businessUnit: BusinessUnit) =>
                                deleteBusinessUnit(businessUnit.id),
                            render: (businessUnit: BusinessUnit) => (
                                <TrashCan className="cursor-pointer flex" id={businessUnit.id} />
                            ),
                        },
                    ]}
                />
            </div>
        </div>
    );
}
